import type { ChartSpec } from "@/types";
import { formatEmissionUnit } from "@/lib/emissionUnits";

/** Describe plotted values, not arbitrary metadata returned by a data source. */
export function chartScreenSummary(spec: ChartSpec): {
  text: string;
  truncated: boolean;
} {
  const parts = [`${spec.title} (${spec.chartType})`];
  let truncated = spec.data.datasets.length > 8;
  for (const [index, dataset] of spec.data.datasets.slice(0, 8).entries()) {
    const unit =
      spec.data.secondaryAxis?.datasetIndex === index
        ? spec.data.secondaryAxis.unit
        : spec.data.unit;
    if (dataset.values.length > 24) truncated = true;
    const points = dataset.values.slice(0, 24).map((value, i) => {
      const label = spec.data.labels[i] ?? `Point ${i + 1}`;
      const rendered =
        value == null
          ? "No configured value"
          : typeof value === "number"
            ? String(value)
            : Object.entries(value)
                .filter(([key]) => ["x", "y", "z", "value"].includes(key))
                .map(
                  ([key, v]) =>
                    `${key}=${Array.isArray(v) ? v.join(", ") : String(v)}`,
                )
                .join("; ");
      return `${label}: ${rendered}${unit ? ` ${formatEmissionUnit(unit)}` : ""}`;
    });
    parts.push(`${dataset.label}: ${points.join(", ")}`);
  }
  if (spec.data.links?.length) {
    if (spec.data.links.length > 24) truncated = true;
    parts.push(
      `Flows: ${spec.data.links
        .slice(0, 24)
        .map(
          (link) =>
            `${link.source} → ${link.target}: ${link.value}${spec.data.unit ? ` ${formatEmissionUnit(spec.data.unit)}` : ""}`,
        )
        .join(", ")}`,
    );
  }
  const all = parts.join("; ");
  if (all.length > 1400) truncated = true;
  return {
    text:
      all.slice(0, 1400) +
      (truncated ? "; Additional chart data omitted." : ""),
    truncated,
  };
}
